import React from "react";
import { ChevronDown } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";

/**
 * ExpandableText
 *
 * A11y-friendly, animated “show more / show less” block for long text.
 * - Truncates to a fixed height with a soft gradient fade.
 * - Expands/collapses with a smooth height animation and rotating arrow.
 * - Keyboard and screen-reader accessible via <button> with aria-* attrs.
 * - Respects reduced-motion user preference.
 */
export function ExpandableText({
  children,
  collapsedHeight = 128, // px
  expandLabel = "Zobraziť viac",
  collapseLabel = "Zobraziť menej",
  className = "",
  gradient = false,
  id,
  onToggle,
}: {
  children: React.ReactNode;
  /** Height (in px) shown while collapsed. */
  collapsedHeight?: number;
  /** Button label when collapsed. */
  expandLabel?: string;
  /** Button label when expanded. */
  collapseLabel?: string;
  /** Optional extra class names on the wrapper. */
  className?: string;
  /** Add a fade gradient when collapsed. */
  gradient?: boolean;
  /** Optional id to link aria-controls. */
  id?: string;
  /** Optional id to link aria-controls. */
  onToggle?: (expanded: boolean) => void;
}) {
  const [expanded, setExpanded] = React.useState(false);
  const prefersReducedMotion = useReducedMotion();

  const toggle = () => {
    setExpanded((e) => {
      const next = !e;
      onToggle?.(next);
      return next;
    });
  };

  return (
    <div className={`w-full ${className}`}>
      <div className="relative">
        {/* Animated height wrapper */}
        <motion.div
          id={"contentId"}
          aria-hidden={!expanded}
          initial={false}
          animate={{ height: expanded ? "auto" : collapsedHeight }}
          transition={
            prefersReducedMotion
              ? { duration: 0 }
              : { duration: 0.35, ease: [0.25, 0.8, 0.25, 1] }
          }
          style={{ overflow: "hidden" }}
        >
          <div className="prose max-w-none dark:prose-invert">{children}</div>
        </motion.div>

        {/* Bottom fade when collapsed */}
        {gradient && !expanded && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-b from-transparent to-white/95 dark:to-neutral-900/95"
          />
        )}
      </div>

      {/* Toggle button */}
      <button
        type="button"
        className="mt-3 inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-sm font-medium shadow-sm hover:shadow transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-black/30 dark:focus-visible:ring-white/30"
        onClick={toggle}
        aria-expanded={expanded}
        aria-controls={"contentId"}
      >
        <span>{expanded ? collapseLabel : expandLabel}</span>
        <motion.span
          initial={false}
          animate={{ rotate: expanded ? 180 : 0 }}
          transition={
            prefersReducedMotion ? { duration: 0 } : { duration: 0.25 }
          }
          className="inline-flex"
        >
          <ChevronDown size={16} aria-hidden />
        </motion.span>
      </button>
    </div>
  );
}

/** Demo / Usage */
export default function Demo() {
  return (
    <div className="min-h-screen bg-white dark:bg-neutral-950 text-neutral-900 dark:text-neutral-50 p-6">
      <div className="mx-auto max-w-2xl space-y-6">
        <h1 className="text-2xl font-semibold">Form with collapsible intro</h1>

        <ExpandableText
          collapsedHeight={140}
          expandLabel="Read details"
          collapseLabel="Hide details"
        >
          <p>
            This is a long description that sets context for a form. Users often
            don’t need the entire wall of text—so we collapse it by default and
            let them expand if they’re curious. The component animates the
            height between a fixed collapsed size and the full content, with a
            soft gradient hinting at more content below.
          </p>
          <p>
            Accessibility: The toggle is a real <code>button</code> with{" "}
            <code>aria-expanded</code> and
            <code>aria-controls</code>. Keyboard users can press Enter/Space to
            open it. Motion respects the user’s{" "}
            <code>prefers-reduced-motion</code> setting. You can customize the
            collapsed height and labels.
          </p>
          <ul>
            <li>Drop it above your form to keep the form in view.</li>
            <li>Arrow rotates on expand/collapse.</li>
            <li>No layout jump thanks to smooth animation.</li>
          </ul>
        </ExpandableText>

        {/* Your form follows and stays visible above the fold */}
        <form className="grid gap-4 rounded-2xl border p-4 shadow-sm">
          <label className="grid gap-1">
            <span className="text-sm">Name</span>
            <input
              className="rounded-xl border px-3 py-2 bg-white/70 dark:bg-neutral-900/70"
              placeholder="Ada Lovelace"
            />
          </label>
          <label className="grid gap-1">
            <span className="text-sm">Email</span>
            <input
              type="email"
              className="rounded-xl border px-3 py-2 bg-white/70 dark:bg-neutral-900/70"
              placeholder="ada@example.com"
            />
          </label>
          <label className="grid gap-1">
            <span className="text-sm">Message</span>
            <textarea
              className="rounded-xl border px-3 py-2 bg-white/70 dark:bg-neutral-900/70"
              rows={4}
              placeholder="How can we help?"
            />
          </label>
          <div className="flex items-center justify-end gap-3">
            <button type="reset" className="rounded-xl border px-4 py-2">
              Reset
            </button>
            <button
              type="submit"
              className="rounded-xl bg-black text-white px-4 py-2 shadow hover:shadow-md"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
